// HeroSlider.js - The first slide paints straight away; the rest mount once the page is idle
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './HeroSlider.css';

const slides = [
  { 
    id: 0, title: "AALITECH", subtitle: "Driving Digital Innovation Forward", desc: "Next‑gen tech solutions & digital transformation", link: "/companies/aalitech", btn: "Explore Tech Solutions",
    imageDesktop: "/assets/aalitech_1.webp",
    imageTablet: "/assets/opt/hero-aalitech-m.webp",
    imageMobile: "/assets/opt/hero-aalitech-m.webp"
  },
  { 
    id: 1, title: "AALI CONSSULTANCY", subtitle: "Careers Built. Futures Placed.", desc: "Job placements in India & overseas, plus college admissions", link: "/companies/aaliconssultancy", btn: "Explore Opportunities",
    imageDesktop: "/assets/opt/hero-consultancy.webp",
    imageTablet: "/assets/opt/hero-consultancy-t.webp",
    imageMobile: "/assets/opt/hero-consultancy-t.webp"
  },
  { 
    id: 2, title: "AALI BUILDERS", subtitle: "Building Tomorrow's Global Infrastructure", desc: "Sustainable development & engineering excellence", link: "/companies/aalibuilders", btn: "View Projects",
    imageDesktop: "/assets/opt/hero-builders.webp",
    imageTablet: "/assets/opt/hero-builders-m.webp",
    imageMobile: "/assets/opt/hero-builders-m.webp"
  },
  { 
    id: 3, title: "AALI MOVIES", subtitle: "Cinematic Brilliance. Stories That Last.", desc: "Film production house creating global impact", link: "/companies/aalimovies", btn: "Explore Films",
    imageDesktop: "/assets/opt/hero-movies.webp",
    imageTablet: "/assets/opt/hero-movies-m.webp",
    imageMobile: "/assets/opt/hero-movies-m.webp"
  },
  { 
    id: 4, title: "AALI GOLD JEWELZ", subtitle: "Eternal Elegance. Masterful Craftsmanship.", desc: "Luxury jewellery brand redefining opulence", link: "/companies/aaligoldjewelz", btn: "Discover Collection",
    imageDesktop: "/assets/opt/hero-gold.webp",
    imageTablet: "/assets/opt/hero-gold-t.webp",
    imageMobile: "/assets/opt/hero-gold-m.webp"
  },
  { 
    id: 5, title: "AALI MUSIC", subtitle: "Rhythm of Excellence. Timeless Melodies.", desc: "Music label & audio production house", link: "/companies/aalimusic", btn: "Listen Now",
    imageDesktop: "/assets/opt/hero-music.webp",
    imageTablet: "/assets/opt/hero-music-m.webp",
    imageMobile: "/assets/opt/hero-music-m.webp"
  },
  { 
    id: 6, title: "AALI TRUSTZ", subtitle: "Empowering Communities. Positive Change.", desc: "Social impact & sustainable development", link: "/companies/aalitrustz", btn: "Join the Mission",
    imageDesktop: "/assets/opt/hero-trustz.webp",
    imageTablet: "/assets/opt/hero-trustz-t.webp",
    imageMobile: "/assets/opt/hero-trustz-m.webp"
  },
  { 
    id: 7, title: "AALI FILMZ", subtitle: "Creative Storytelling. Digital Excellence.", desc: "Digital content studio for modern narratives", link: "/companies/aalifilmz", btn: "Watch Our Work",
    imageDesktop: "/assets/opt/hero-filmz.webp",
    imageTablet: "/assets/opt/hero-filmz-t.webp",
    imageMobile: "/assets/opt/hero-filmz-t.webp"
  }
];

// Get image URL based on viewport
const getImageUrl = (slide, type = 'desktop') => {
  if (type === 'mobile') return slide.imageMobile;
  if (type === 'tablet') return slide.imageTablet;
  return slide.imageDesktop;
};

// Get current breakpoint
const getBreakpoint = () => {
  const width = window.innerWidth;
  if (width <= 480) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
};

const HeroSlider = () => {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);

  // The first slide's photo is in the DOM from the very first paint, so it is
  // the LCP element. The other seven only mount once the page has finished
  // loading and the browser is idle — that keeps ~1.7 MB of photos from
  // competing with the critical CSS and JS during startup.
  const [restMounted, setRestMounted] = useState(false);

  const autoTimerRef = useRef(null);
  const totalSlides = slides.length;
  const previewCount = 4;

  // ── Mount the remaining slides after load, when the browser is idle ───────
  useEffect(() => {
    let cancelled = false;
    let idleId = null;

    const mountRest = () => {
      const schedule =
        window.requestIdleCallback || ((cb) => window.setTimeout(cb, 200));
      idleId = schedule(() => { if (!cancelled) setRestMounted(true); });
    };

    if (document.readyState === 'complete') {
      mountRest();
    } else {
      window.addEventListener('load', mountRest);
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', mountRest);
      if (idleId != null && window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, []);

  // ── Navigation helpers ───────────────────────────────────────────────────
  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % totalSlides);
    setProgress(0);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrent((prev) => (prev - 1 + totalSlides) % totalSlides);
    setProgress(0);
  }, [totalSlides]);

  const goToSlide = useCallback((index) => {
    if (index === current) return;
    setCurrent(index);
    setProgress(0);
  }, [current]);

  // ── Autoplay — held back until the other slides exist, so it can never
  //    advance onto a slide that has no photo yet ───────────────────────────
  useEffect(() => {
    if (!restMounted) return;

    if (autoTimerRef.current) clearInterval(autoTimerRef.current);

    autoTimerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          nextSlide();
          return 0;
        }
        return prev + 1;
      });
    }, 50); // 5 seconds total (100 steps x 50 ms)

    return () => {
      if (autoTimerRef.current) clearInterval(autoTimerRef.current);
    };
  }, [restMounted, nextSlide, current]);

  // ── Preview strip: the next few slides after the current one ─────────────
  const previewSlides = [];
  if (restMounted) {
    for (let n = 1; n < totalSlides && previewSlides.length < previewCount; n++) {
      previewSlides.push(slides[(current + n) % totalSlides]);
    }
  }

  const currentBreakpoint = getBreakpoint();

  return (
    <div className="hero-container">
      <div className="slider-wrapper">
        {slides.map((slide, idx) => {
          // Only the first slide exists during startup
          if (idx !== 0 && !restMounted) return null;

          const isActive = idx === current;
          return (
            <div key={slide.id} className={`slide ${isActive ? 'active' : ''}`}>
              <img
                src={getImageUrl(slide, currentBreakpoint)}
                alt={slide.title}
                className="slide-image"
                fetchpriority={idx === 0 ? 'high' : 'low'}
                decoding="async"
              />
              <div className="content">
                <h2 className={isActive ? 'animate-text' : ''}>{slide.title}</h2>
                <p className={isActive ? 'animate-text' : ''}>{slide.desc}</p>
                <Link to={slide.link} className={`cta-btn ${isActive ? 'animate-text' : ''}`}>
                  {slide.btn}
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      <div className="controls-container">
        <div className="preview-strip">
          {previewSlides.map((slide) => (
            <div
              key={slide.id}
              className="preview-box"
              onClick={() => goToSlide(slide.id)}
              role="button"
              tabIndex={0}
              aria-label={`Show ${slide.title} slide`}
              onKeyDown={(e) => { if (e.key === 'Enter') goToSlide(slide.id); }}
            >
              {/* Same file as the main slide - already downloaded, so no blank box */}
              <img
                src={getImageUrl(slide, currentBreakpoint)}
                alt={`Preview ${slide.title}`}
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>
        <div className="navigation-bar">
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
          <div className="nav-buttons">
            <button className="nav-btn" onClick={prevSlide} aria-label="Previous slide">
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
            <button className="nav-btn" onClick={nextSlide} aria-label="Next slide">
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSlider;
